import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import StationsOnMap from 'components/StationsOnMap/StationsOnMap'
import Title from 'components/Title/Title'
import { Point } from 'geojson'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, IRobotInfo, IStation } from 'utils/interfaces'

import AddRobotModal from './AddRobotModal/AddRobotModal'
import AllRobotsOnMap from './AllRobotsOnMap/AllRobotsOnMap'
import RobotItem from './RobotItem/RobotItem'

import style from './RobotListView.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

const RobotListView = ({
  robots,
  stations,
  onRobotsUpdated,
  onStationsChanged,
  searchQuery,
  setSearchQuery,
  currentPage,
  setCurrentPage,
  totalCount,
  pageSize,
  pageInput,
  setPageInput,
  handlePageJump,
  loadingProgress,
}: {
  robots: IRobotInfo[]
  stations: IStation[]
  onRobotsUpdated: () => void
  onStationsChanged: () => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  totalCount: number
  pageSize: number
  pageInput: string
  setPageInput: (val: string) => void
  handlePageJump: (e: React.KeyboardEvent<HTMLInputElement>) => void
  loadingProgress: PROGRESS_TYPES
}) => {
  const { t } = useTranslation()
  const [addingNewRobot, setAddingNewRobot] = useState(false)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)
  const [highlightedRobotId, setHighlightedRobotId] = useState<number | undefined>(undefined)

  const onMapInited = () => {
    const map = mapRef.current?.getMap()
    if (map) {
      setMap(map)
    }
  }

  return (
    <div className={style.container}>
      <div className={pageLayoutStyle.pageHeaderArea}>
        <div className={style.titleBlock}>
          <Title text={t('robots.title')} />{' '}
          <Button.Standart className={style.button} onClick={() => setAddingNewRobot(true)}>
            {t('robots.add_robot')}
          </Button.Standart>
        </div>
        <div className={style.controlsBlock}>
          <input
            className={style.searchInput}
            type="text"
            placeholder="Search robots..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div className={style.main}>
        <Box className={style.box}>
          {loadingProgress === PROGRESS_TYPES.WORK ? (
            <Loader />
          ) : loadingProgress === PROGRESS_TYPES.ERROR ? (
            <div className={style.emptyWrapper}>
              <div className={style.emptyTitle}>{t('common.error')}</div>
            </div>
          ) : (
            <div className={style.tableWrapper}>
              <div className={style.tableHeader}>
                <div></div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('robots.serial_id')}</div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('robots.name')}</div>
                <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('robots.battery')}</div>
                <div className={clsx(style.tableHeaderItemCentered, 'text-16-400')}>{t('robots.mode')}</div>
                <div></div>
              </div>
              {robots.length ? (
                <div className={style.table}>
                  {robots.map(robot => (
                    <RobotItem
                      key={robot.id}
                      stations={stations}
                      robot={robot}
                      onRobotUpdated={onRobotsUpdated}
                      hightlighted={robot.id === highlightedRobotId}
                      onStationsChanged={onStationsChanged}
                      robots={robots}
                    />
                  ))}
                </div>
              ) : (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>{t('robots.no_robots_title')}</div>
                  <div className={style.emptyDescription}>{t('robots.no_robots_description')}</div>
                </div>
              )}

              {totalCount > pageSize && (
                <div className={style.pagination}>
                  <button
                    className={style.paginationButton}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &larr;
                  </button>
                  <span className={style.pageInfo}>
                    <input
                      className={style.pageInput}
                      value={pageInput}
                      onChange={e => setPageInput(e.target.value)}
                      onKeyDown={handlePageJump}
                      onBlur={() => setPageInput(currentPage.toString())}
                    />
                    / {Math.ceil(totalCount / pageSize) || 1}
                  </span>
                  <button
                    className={style.paginationButton}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage * pageSize >= totalCount}
                  >
                    &rarr;
                  </button>
                </div>
              )}
            </div>
          )}
        </Box>
        <MapboxCommon ref={mapRef} disableRuler={true} overlayItems={[]} onMapInited={onMapInited}>
          <StationsOnMap
            map={map}
            stations={stations.filter(station => station.point).map(station => station.point) as Point[]}
          />
          <AllRobotsOnMap map={map} robots={robots} onRobotClicked={setHighlightedRobotId} />
        </MapboxCommon>
      </div>
      <AddRobotModal
        isOpen={addingNewRobot}
        stations={stations}
        onClose={() => setAddingNewRobot(false)}
        onSave={() => {
          onRobotsUpdated()
          setAddingNewRobot(false)
        }}
        onStationsChanged={onStationsChanged}
        robots={robots}
      />
    </div>
  )
}

export default RobotListView
